import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth, functions } from '../lib/firebase';
import type { 
  LoginWithAccessCodeRequest, 
  LoginResponse, 
  GalleryUser 
} from '@shared/types/auth';
import { generateSessionId, createSessionData } from '@shared/utils/analytics';

interface ClientAuthResult {
  success: boolean;
  user?: GalleryUser;
  sessionId?: string;
  error?: string;
}

class AuthService {
  private currentUser: GalleryUser | null = null;
  private currentSessionId: string | null = null;
  private galleryId: string | null = null;

  /**
   * Login with access code - matches Unity client flow
   */
  async loginWithAccessCode(accessCode: string, galleryId: string): Promise<ClientAuthResult> {
    try {
      // Call the same Firebase function that Unity client uses
      const loginFunction = httpsCallable<LoginWithAccessCodeRequest, LoginResponse>(
        functions, 
        'loginWithAccessCode'
      );

      const result = await loginFunction({
        accessCode: accessCode.toUpperCase(),
        galleryId
      });

      if (!result.data.success) {
        return {
          success: false,
          error: result.data.message || 'Invalid access code'
        };
      }

      if (!auth || !result.data.token || !result.data.user) {
        return {
          success: false,
          error: 'Access code login is not configured correctly.'
        };
      }

      await signInWithCustomToken(auth, result.data.token);

      // Create user object matching Unity client structure
      const user: GalleryUser = {
        userId: result.data.user.id,
        displayName: result.data.user.displayName,
        firstName: result.data.user.firstName,
        lastName: result.data.user.lastName,
        collectionId: result.data.user.collectionId,
        collectionName: result.data.user.collectionName,
        hideTitles: result.data.user.hideTitles,
        codeValue: accessCode.toUpperCase(),
        active: true,
        userEmail: '', // Not used in client
        dateCreatedUTC: null,
        lastLoginTimeUTC: new Date().toISOString()
      };

      // Generate session ID
      const sessionId = generateSessionId(user.userId!);

      // Store authentication state
      this.currentUser = user;
      this.currentSessionId = sessionId;
      this.galleryId = galleryId;

      // Do not persist session; require access code each visit
      try {
        localStorage.removeItem('artvr_user');
        localStorage.removeItem('artvr_session');
        localStorage.removeItem('artvr_gallery');
      } catch (error) {
        console.debug('Session persistence cleanup skipped:', error);
      }

      return {
        success: true,
        user,
        sessionId
      };

    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: 'Failed to validate access code. Please try again.'
      };
    }
  }

  /**
   * Restore session disabled: require access code each visit
   */
  restoreSession(): ClientAuthResult {
    try {
      localStorage.removeItem('artvr_user');
      localStorage.removeItem('artvr_session');
      localStorage.removeItem('artvr_gallery');
    } catch (error) {
      console.debug('Session persistence cleanup skipped:', error);
    }
    this.currentUser = null;
    this.currentSessionId = null;
    this.galleryId = null;
    return { success: false, error: 'Session persistence disabled' };
  }

  /**
   * Logout and clear session
   */
  async logout(): Promise<void> {
    if (auth) {
      await signOut(auth);
    }
    this.clearSession();
  }

  /**
   * Clear all session data
   */
  private clearSession(): void {
    this.currentUser = null;
    this.currentSessionId = null;
    this.galleryId = null;
    
    localStorage.removeItem('artvr_user');
    localStorage.removeItem('artvr_session');
    localStorage.removeItem('artvr_gallery');
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): GalleryUser | null {
    return this.currentUser;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Get current gallery ID
   */
  getCurrentGalleryId(): string | null {
    return this.galleryId;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null && this.currentSessionId !== null;
  }

  /**
   * Get session data for analytics
   */
  getSessionData() {
    if (!this.currentUser || !this.currentSessionId) return null;

    return createSessionData(
      this.currentUser.userId!,
      this.currentUser.codeValue,
      this.currentSessionId
    );
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
